<template>
  <head>
    <meta charset="utf-8"/>
    <title>Desk Birdy | Building Plans Dashboard</title>
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <meta content="County Building Plans Management System by Nouveta LTD" name="description"/>
    <meta content="Kelvin Njuguna" name="author"/>
    <link rel="shortcut icon" href="assets/images/favicon.svg">
    <link href="assets/css/bootstrap.min.css" id="bootstrap-style" rel="stylesheet" type="text/css"/>
    <link href="assets/css/icons.min.css" rel="stylesheet" type="text/css"/>
    <link href="assets/css/app.min.css" id="app-style" rel="stylesheet" type="text/css"/>
    <link href="assets/css/custom.css" id="app-style" rel="stylesheet" type="text/css"/>
  </head>

  <body data-sidebar="dark">
  <div id="layout-wrapper">
    <NavigationBar/>

    <div class="content-minimize">
      <div class="page-content">
        <div class="container-fluid">

          <!-- Page Title -->
          <div class="row">
            <div class="col-12">
              <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 class="mb-sm-0">
                  <i class="mdi mdi-floor-plan me-2 text-primary"></i>Building Plans Dashboard
                </h4>
                <div class="page-title-right">
                  <ol class="breadcrumb m-0">
                    <li class="breadcrumb-item"><a href="javascript: void(0);">Dashboard</a></li>
                    <li class="breadcrumb-item active">Building Plans</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          <!-- Plan Count Summary Cards -->
          <div class="row">
            <div class="col-xl-3 col-md-6">
              <div class="card card-h-100">
                <div class="card-body">
                  <div class="d-flex align-items-center">
                    <div class="flex-grow-1">
                      <span class="text-muted mb-3 lh-1 d-block text-truncate">Total / Potential</span>
                      <h4 class="mb-3">
                        <span class="counter-value">{{ numberFormat(stats.planCountSummary.potentialBusinesses) }}</span>
                      </h4>
                      <div class="text-nowrap">
                        <span class="badge" :class="planApprovalRate >= 70 ? 'bg-success-subtle text-success' : planApprovalRate >= 50 ? 'bg-warning-subtle text-warning' : 'bg-danger-subtle text-danger'">
                          {{ planApprovalRate }}% Approved
                        </span>
                      </div>
                    </div>
                    <div class="flex-shrink-0 text-end dash-widget">
                      <div class="avatar-sm rounded-circle bg-primary-subtle">
                        <span class="avatar-title bg-primary-subtle text-primary rounded-circle fs-2">
                          <i class="mdi mdi-floor-plan"></i>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-xl-3 col-md-6">
              <div class="card card-h-100">
                <div class="card-body">
                  <div class="d-flex align-items-center">
                    <div class="flex-grow-1">
                      <span class="text-muted mb-3 lh-1 d-block text-truncate">Active Plans</span>
                      <h4 class="mb-3">
                        <span class="counter-value text-success">{{ numberFormat(stats.planCountSummary.approvedPlans) }}</span>
                      </h4>
                      <div class="text-nowrap">
                        <span class="badge bg-success-subtle text-success">
                          <i class="mdi mdi-check-circle me-1"></i>Approved
                        </span>
                      </div>
                    </div>
                    <div class="flex-shrink-0 text-end dash-widget">
                      <div class="avatar-sm rounded-circle bg-success-subtle">
                        <span class="avatar-title bg-success-subtle text-success rounded-circle fs-2">
                          <i class="mdi mdi-check-decagram"></i>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-xl-3 col-md-6">
              <div class="card card-h-100">
                <div class="card-body">
                  <div class="d-flex align-items-center">
                    <div class="flex-grow-1">
                      <span class="text-muted mb-3 lh-1 d-block text-truncate">Pending Approval</span>
                      <h4 class="mb-3">
                        <span class="counter-value text-warning">{{ numberFormat(stats.planCountSummary.awaitingApproval) }}</span>
                      </h4>
                      <div class="text-nowrap">
                        <span class="badge bg-warning-subtle text-warning">
                          <i class="mdi mdi-clock-outline me-1"></i>In Progress
                        </span>
                      </div>
                    </div>
                    <div class="flex-shrink-0 text-end dash-widget">
                      <div class="avatar-sm rounded-circle bg-warning-subtle">
                        <span class="avatar-title bg-warning-subtle text-warning rounded-circle fs-2">
                          <i class="mdi mdi-progress-clock"></i>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-xl-3 col-md-6">
              <div class="card card-h-100">
                <div class="card-body">
                  <div class="d-flex align-items-center">
                    <div class="flex-grow-1">
                      <span class="text-muted mb-3 lh-1 d-block text-truncate">Expiring (30 Days)</span>
                      <h4 class="mb-3">
                        <span class="counter-value" :class="stats.planCountSummary.expiringIn30Days > 0 ? 'text-danger' : 'text-muted'">
                          {{ numberFormat(stats.planCountSummary.expiringIn30Days) }}
                        </span>
                      </h4>
                      <div class="text-nowrap">
                        <span class="badge bg-info-subtle text-info">
                          <i class="mdi mdi-calendar-alert me-1"></i>Renewal Alert
                        </span>
                      </div>
                    </div>
                    <div class="flex-shrink-0 text-end dash-widget">
                      <div class="avatar-sm rounded-circle bg-info-subtle">
                        <span class="avatar-title bg-info-subtle text-info rounded-circle fs-2">
                          <i class="mdi mdi-alert-circle-outline"></i>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Financial Summary Cards -->
          <div class="row">
            <div class="col-xl-3 col-md-6">
              <div class="card">
                <div class="card-body">
                  <div class="d-flex align-items-center">
                    <div class="avatar-sm flex-shrink-0">
                      <span class="avatar-title bg-light text-primary rounded-circle fs-3">
                        <i class="mdi mdi-calendar-today"></i>
                      </span>
                    </div>
                    <div class="flex-grow-1 ms-3">
                      <p class="text-muted text-uppercase fw-semibold fs-12 mb-1">Today's Collections</p>
                      <h4 class=" mb-0">KES <span class="counter-value">{{ numberFormat(stats.financialSummary.todaysCollections) }}</span></h4>
                      <p class="text-muted mb-0 mt-1">
                        <span class="badge" :class="growthClass(stats.financialSummary.todayGrowth)">
                          <i :class="stats.financialSummary.todayGrowth >= 0 ? 'mdi mdi-arrow-up' : 'mdi mdi-arrow-down'"></i>
                          {{ Math.abs(stats.financialSummary.todayGrowth) }}%
                        </span>
                        <span class="text-muted ms-2">vs yesterday</span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-xl-3 col-md-6">
              <div class="card">
                <div class="card-body">
                  <div class="d-flex align-items-center">
                    <div class="avatar-sm flex-shrink-0">
                      <span class="avatar-title bg-light text-primary rounded-circle fs-3">
                        <i class="mdi mdi-calendar-week"></i>
                      </span>
                    </div>
                    <div class="flex-grow-1 ms-3">
                      <p class="text-muted text-uppercase fw-semibold fs-12 mb-1">This Week</p>
                      <h4 class=" mb-0">KES <span class="counter-value">{{ numberFormat(stats.financialSummary.thisWeeksCollections) }}</span></h4>
                      <p class="text-muted mb-0 mt-1">
                        <span class="badge" :class="growthClass(stats.financialSummary.weekGrowth)">
                          <i :class="stats.financialSummary.weekGrowth >= 0 ? 'mdi mdi-arrow-up' : 'mdi mdi-arrow-down'"></i>
                          {{ Math.abs(stats.financialSummary.weekGrowth) }}%
                        </span>
                        <span class="text-muted ms-2">vs last week</span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-xl-3 col-md-6">
              <div class="card">
                <div class="card-body">
                  <div class="d-flex align-items-center">
                    <div class="avatar-sm flex-shrink-0">
                      <span class="avatar-title bg-light text-primary rounded-circle fs-3">
                        <i class="mdi mdi-calendar-month"></i>
                      </span>
                    </div>
                    <div class="flex-grow-1 ms-3">
                      <p class="text-muted text-uppercase fw-semibold fs-12 mb-1">This Month</p>
                      <h4 class=" mb-0">KES <span class="counter-value">{{ numberFormat(stats.financialSummary.thisMonthsCollections) }}</span></h4>
                      <p class="text-muted mb-0 mt-1">
                        <span class="badge" :class="growthClass(stats.financialSummary.monthGrowth)">
                          <i :class="stats.financialSummary.monthGrowth >= 0 ? 'mdi mdi-arrow-up' : 'mdi mdi-arrow-down'"></i>
                          {{ Math.abs(stats.financialSummary.monthGrowth) }}%
                        </span>
                        <span class="text-muted ms-2">vs last month</span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-xl-3 col-md-6">
              <div class="card">
                <div class="card-body">
                  <div class="d-flex align-items-center">
                    <div class="avatar-sm flex-shrink-0">
                      <span class="avatar-title bg-light text-primary rounded-circle fs-3">
                        <i class="mdi mdi-calendar-range"></i>
                      </span>
                    </div>
                    <div class="flex-grow-1 ms-3">
                      <p class="text-muted text-uppercase fw-semibold fs-12 mb-1">Financial Year</p>
                      <h4 class=" mb-0">KES <span class="counter-value">{{ numberFormat(stats.financialSummary.financialYearCollections) }}</span></h4>
                      <p class="text-muted mb-0 mt-1">
                        <span class="badge bg-info-subtle text-info">
                          <i class="mdi mdi-chart-line"></i>
                          {{ fyProgress }}% Progress
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Charts Row -->
          <div class="row">
            <!-- Monthly Revenue Performance -->
            <div class="col-xl-8">
              <div class="card">
                <div class="card-header">
                  <h4 class="card-title mb-0">
                    <i class="mdi mdi-chart-bar me-2 text-primary"></i>Monthly Revenue Performance
                  </h4>
                </div>
                <div class="card-body">
                  <div id="revenueChart" style="min-height: 350px;"></div>
                </div>
              </div>
            </div>

            <!-- Payment Methods Distribution -->
            <div class="col-xl-4">
              <div class="card">
                <div class="card-header">
                  <h4 class="card-title mb-0">
                    <i class="mdi mdi-credit-card me-2 text-primary"></i>Payment Methods
                  </h4>
                </div>
                <div class="card-body">
                  <div id="paymentModesChart" style="min-height: 350px;"></div>
                  <div class="table-responsive mt-3">
                    <table class="table table-sm mb-0">
                      <tbody>
                      <tr v-for="(mode, index) in stats.paymentModes" :key="index">
                        <td>{{ mode.payment_type }}</td>
                        <td class="text-end">{{ numberFormat(mode.transactionCount) }} txns</td>
                        <td class="text-end"><strong>{{ mode.percentage }}%</strong></td>
                      </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Top Sub-Counties -->
          <div class="row">
            <div class="col-12">
              <div class="card">
                <div class="card-header bg-transparent">
                  <div class="d-flex justify-content-between align-items-center">
                    <h4 class="card-title mb-0">
                      <i class="mdi mdi-trophy me-2 text-warning"></i>Top 10 Sub-Counties
                    </h4>
                    <span class="badge bg-primary">{{ stats.topSubCounties ? stats.topSubCounties.length : 0 }} Sub-Counties</span>
                  </div>
                </div>
                <div class="card-body">
                  <div class="table-responsive">
                    <table class="table table-hover align-middle table-nowrap mb-0">
                      <thead class="table-light">
                      <tr>
                        <th>Rank</th>
                        <th>Sub-County</th>
                        <th class="text-center">Transactions</th>
                        <th class="text-end">Total Revenue</th>
                        <th class="text-center">Active Plans</th>
                        <th>Performance</th>
                      </tr>
                      </thead>
                      <tbody>
                      <tr v-for="(county, index) in stats.topSubCounties" :key="index">
                        <td>
                          <div class="avatar-xs">
                              <span class="avatar-title rounded-circle" :class="getRankClass(index)">
                                {{ index + 1 }}
                              </span>
                          </div>
                        </td>
                        <td>
                          <h6 class="mb-0">{{ county.subCountyName }}</h6>
                          <small class="text-muted">Avg: KES {{ numberFormat(county.avgDailyRevenue) }}/day</small>
                        </td>
                        <td class="text-center">
                          <span class="badge bg-primary-subtle text-primary">{{ numberFormat(county.totalTransactions) }}</span>
                        </td>
                        <td class="text-end">
                          <h6 class="mb-0 text-primary">KES {{ numberFormat(county.totalRevenue) }}</h6>
                        </td>
                        <td class="text-center">
                          <span class="badge bg-success-subtle text-success">{{ numberFormat(county.activeBusinesses) }}</span>
                        </td>
                        <td style="width: 200px;">
                          <div class="progress progress-sm">
                            <div class="progress-bar bg-primary" :style="{width: (county.totalRevenue / stats.topSubCounties[0].totalRevenue * 100) + '%'}"></div>
                          </div>
                        </td>
                      </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Top Revenue Officers Leaderboard -->
          <div class="row">
            <div class="col-12">
              <div class="card">
                <div class="card-header bg-transparent">
                  <div class="d-flex justify-content-between align-items-center">
                    <h4 class="card-title mb-0">
                      <i class="mdi mdi-account-star me-2 text-success"></i>Top 10 Revenue Officers Leaderboard
                    </h4>
                    <span class="badge bg-success">{{ stats.topOfficers ? stats.topOfficers.length : 0 }} Officers</span>
                  </div>
                </div>
                <div class="card-body">
                  <div class="table-responsive">
                    <table class="table table-hover align-middle mb-0">
                      <thead class="table-light">
                      <tr>
                        <th>Rank</th>
                        <th>Officer Name</th>
                        <th class="text-center">Total Transactions</th>
                        <th class="text-end">Total Revenue</th>
                        <th class="text-end">Today's Revenue</th>
                        <th class="text-end">This Month</th>
                        <th class="text-end">Avg/Transaction</th>
                      </tr>
                      </thead>
                      <tbody>
                      <tr v-for="(officer, index) in stats.topOfficers" :key="index">
                        <td>
                          <div class="avatar-xs">
                              <span class="avatar-title rounded-circle" :class="getRankBadgeClass(index)">
                                <i v-if="index === 0" class="mdi mdi-trophy text-white"></i>
                                <i v-else-if="index === 1" class="mdi mdi-medal text-white"></i>
                                <i v-else-if="index === 2" class="mdi mdi-seal text-white"></i>
                                <span v-else>{{ index + 1 }}</span>
                              </span>
                          </div>
                        </td>
                        <td>
                          <h6 class="mb-0">{{ officer.createdByName }}</h6>
                        </td>
                        <td class="text-center">
                          <span class="badge bg-info-subtle text-info">{{ numberFormat(officer.totalTransactions) }}</span>
                        </td>
                        <td class="text-end">
                          <h6 class="mb-0 text-primary">KES {{ numberFormat(officer.totalRevenue) }}</h6>
                        </td>
                        <td class="text-end">
                            <span class="badge" :class="officer.todayRevenue > 0 ? 'bg-success-subtle text-success' : 'bg-secondary-subtle text-secondary'">
                              KES {{ numberFormat(officer.todayRevenue) }}
                            </span>
                        </td>
                        <td class="text-end">
                          <strong>KES {{ numberFormat(officer.monthRevenue) }}</strong>
                        </td>
                        <td class="text-end text-muted">
                          KES {{ numberFormat(officer.avgTransaction) }}
                        </td>
                      </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Sub-County Performance Statistics -->
          <div class="row">
            <div class="col-12">
              <div class="card">
                <div class="card-header bg-transparent">
                  <h4 class="card-title mb-0">
                    <i class="mdi mdi-map-marker-multiple me-2 text-info"></i>Sub-County Performance Statistics
                  </h4>
                </div>
                <div class="card-body">
                  <div class="table-responsive" style="max-height: 500px; overflow-y: auto;">
                    <table class="table table-hover table-striped align-middle mb-0">
                      <thead class="table-light sticky-top">
                      <tr>
                        <th>Sub-County</th>
                        <th class="text-end">Current Month</th>
                        <th class="text-center">Transactions</th>
                        <th class="text-end">Last Month</th>
                        <th class="text-center">Growth</th>
                        <th class="text-end">Total Revenue</th>
                        <th class="text-center">Active Plans</th>
                      </tr>
                      </thead>
                      <tbody>
                      <tr v-for="(stat, index) in stats.subCountyStatistics" :key="index">
                        <td>
                          <h6 class="mb-0">{{ stat.subCountyName }}</h6>
                        </td>
                        <td class="text-end">
                          <strong class="text-primary">KES {{ numberFormat(stat.currentMonthRevenue) }}</strong>
                        </td>
                        <td class="text-center">
                          <span class="badge bg-primary-subtle text-primary">{{ numberFormat(stat.currentMonthCount) }}</span>
                        </td>
                        <td class="text-end text-muted">
                          KES {{ numberFormat(stat.lastMonthRevenue) }}
                        </td>
                        <td class="text-center">
                            <span v-if="stat.lastMonthRevenue > 0"
                                  class="badge"
                                  :class="((stat.currentMonthRevenue - stat.lastMonthRevenue) / stat.lastMonthRevenue * 100) >= 0 ? 'bg-success-subtle text-success' : 'bg-danger-subtle text-danger'">
                              <i :class="((stat.currentMonthRevenue - stat.lastMonthRevenue) / stat.lastMonthRevenue * 100) >= 0 ? 'mdi mdi-arrow-up' : 'mdi mdi-arrow-down'"></i>
                              {{ Math.abs(((stat.currentMonthRevenue - stat.lastMonthRevenue) / stat.lastMonthRevenue * 100).toFixed(1)) }}%
                            </span>
                          <span v-else class="badge bg-secondary-subtle text-secondary">N/A</span>
                        </td>
                        <td class="text-end">
                          <h6 class="mb-0">KES {{ numberFormat(stat.totalRevenue) }}</h6>
                        </td>
                        <td class="text-center">
                          <span class="badge bg-success-subtle text-success">{{ numberFormat(stat.activePlans) }}</span>
                        </td>
                      </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Top Service Types -->
          <div class="row">
            <div class="col-12">
              <div class="card">
                <div class="card-header bg-transparent">
                  <div class="d-flex justify-content-between align-items-center">
                    <h4 class="card-title mb-0">
                      <i class="mdi mdi-star me-2 text-warning"></i>Top Revenue Generating Service Types
                    </h4>
                    <span class="badge bg-warning">{{ stats.topServiceTypes ? stats.topServiceTypes.length : 0 }} Services</span>
                  </div>
                </div>
                <div class="card-body">
                  <div class="table-responsive">
                    <table class="table table-hover align-middle mb-0">
                      <thead class="table-light">
                      <tr>
                        <th>#</th>
                        <th>Service Type</th>
                        <th class="text-center">Transactions</th>
                        <th class="text-end">Average Amount</th>
                        <th class="text-end">This Month</th>
                        <th class="text-end">Total Revenue</th>
                      </tr>
                      </thead>
                      <tbody>
                      <tr v-for="(service, index) in stats.topServiceTypes" :key="index">
                        <td>
                          <div class="avatar-xs">
                              <span class="avatar-title rounded-circle bg-warning-subtle text-warning">
                                {{ index + 1 }}
                              </span>
                          </div>
                        </td>
                        <td>
                          <h6 class="mb-0">{{ service.incomeTypeDescription }}</h6>
                        </td>
                        <td class="text-center">
                          <span class="badge bg-primary-subtle text-primary">{{ numberFormat(service.transactionCount) }}</span>
                        </td>
                        <td class="text-end text-muted">
                          KES {{ numberFormat(service.avgAmount) }}
                        </td>
                        <td class="text-end">
                          <strong class="text-success">KES {{ numberFormat(service.currentMonthRevenue) }}</strong>
                        </td>
                        <td class="text-end">
                          <h6 class="mb-0 text-primary">KES {{ numberFormat(service.totalRevenue) }}</h6>
                        </td>
                      </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 30-Day Collection Trend -->
          <div class="row">
            <div class="col-12">
              <div class="card">
                <div class="card-header">
                  <h4 class="card-title mb-0">
                    <i class="mdi mdi-chart-areaspline me-2 text-primary"></i>30-Day Collection Trend
                  </h4>
                </div>
                <div class="card-body">
                  <div id="dailyTrendChart" style="min-height: 300px;"></div>
                </div>
              </div>
            </div>
          </div>

          <!-- Application Status Breakdown -->
          <div class="row">
            <div class="col-12">
              <div class="card">
                <div class="card-header bg-transparent">
                  <div class="d-flex justify-content-between align-items-center">
                    <h4 class="card-title mb-0">
                      <i class="mdi mdi-clipboard-list me-2 text-info"></i>Application Status Breakdown
                    </h4>
                    <span class="badge bg-info">{{ stats.applicationStatuses ? stats.applicationStatuses.length : 0 }} Statuses</span>
                  </div>
                </div>
                <div class="card-body">
                  <div class="table-responsive">
                    <table class="table table-hover align-middle mb-0">
                      <thead class="table-light">
                      <tr>
                        <th>Status</th>
                        <th class="text-center">Plan Count</th>
                        <th class="text-end">Total Value</th>
                        <th class="text-center">Percentage</th>
                      </tr>
                      </thead>
                      <tbody>
                      <tr v-for="(status, index) in stats.applicationStatuses" :key="index">
                        <td>
                          <div class="d-flex align-items-center">
                            <div class="avatar-xs me-2">
                                <span class="avatar-title rounded-circle" :class="getStatusBadgeClass(status.statusName)">
                                  {{ status.statusName ? status.statusName.charAt(0).toUpperCase() : '?' }}
                                </span>
                            </div>
                            <h6 class="mb-0">{{ status.statusName }}</h6>
                          </div>
                        </td>
                        <td class="text-center">
                          <span class="badge" :class="getStatusBadgeClass(status.statusName)">{{ numberFormat(status.businessCount) }}</span>
                        </td>
                        <td class="text-end">
                          <h6 class="mb-0 text-primary">KES {{ numberFormat(status.totalValue) }}</h6>
                        </td>
                        <td class="text-center">
                          <div class="d-flex align-items-center justify-content-center">
                            <span class="me-2">{{ calculateStatusPercentage(status.businessCount) }}%</span>
                            <div class="progress" style="width: 60px; height: 6px;">
                              <div class="progress-bar" :class="getStatusBadgeClass(status.statusName)"
                                   :style="{width: calculateStatusPercentage(status.businessCount) + '%'}"></div>
                            </div>
                          </div>
                        </td>
                      </tr>
                      </tbody>
                      <tfoot class="table-dark">
                      <tr>
                        <th><strong>TOTAL</strong></th>
                        <th class="text-center"><strong>{{ totalStatusBusinesses }}</strong></th>
                        <th class="text-end"><strong>KES {{ numberFormat(totalStatusValue) }}</strong></th>
                        <th class="text-center"><strong>100%</strong></th>
                      </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      <footer class="footer">
        <div class="container-fluid">
          <div class="row">
            <div class="col-sm-6"></div>
            <div class="col-sm-6">
              <div class="text-sm-end d-sm-block">Developed by County</div>
            </div>
          </div>
        </div>
      </footer>

    </div>
  </div>
  </body>
</template>

<script>
/* eslint-disable */
import Highcharts from 'highcharts';
import 'highcharts/modules/drilldown';
import 'highcharts/modules/exporting';
import 'highcharts/modules/export-data';
import 'highcharts/modules/accessibility';
import {execute, econstruction, biller} from "@/api";
import NavigationBar from "@/components/Navigation.vue";

export default {
  name: "EnhancedPlansDashboard",
  components: {
    NavigationBar
  },
  data() {
    return {
      stats: {
        planCountSummary: {
          potentialBusinesses: 0,
          invoicedBusinesses: 0,
          approvedPlans: 0,
          awaitingApproval: 0,
          expiredPlans: 0,
          expiringIn30Days: 0,
          todaysApplications: 0,
          todaysPayments: 0
        },
        financialSummary: {
          totalRevenue: 0,
          todaysCollections: 0,
          yesterdaysCollections: 0,
          thisWeeksCollections: 0,
          lastWeeksCollections: 0,
          thisMonthsCollections: 0,
          lastMonthsCollections: 0,
          financialYearCollections: 0,
          pendingInvoicesSum: 0,
          avgTransactionAmount: 0,
          todayGrowth: 0,
          weekGrowth: 0,
          monthGrowth: 0
        },
        revenuePerformance: [],
        subCountyStatistics: [],
        topSubCounties: [],
        topOfficers: [],
        paymentModes: [],
        dailyTrend: [],
        topServiceTypes: [],
        applicationStatuses: []
      }
    }
  },
  computed: {
    planApprovalRate() {
      if (this.stats.planCountSummary.invoicedBusinesses === 0) return 0;
      return ((this.stats.planCountSummary.approvedPlans /
          this.stats.planCountSummary.invoicedBusinesses) * 100).toFixed(1);
    },
    monthProgress() {
      const now = new Date();
      const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
      return ((now.getDate() / daysInMonth) * 100).toFixed(0);
    },
    fyProgress() {
      return Math.min(100, ((this.stats.financialSummary.financialYearCollections /
          (this.stats.financialSummary.financialYearCollections * 1.2)) * 100).toFixed(0));
    },
    totalStatusBusinesses() {
      if (!this.stats.applicationStatuses) return 0;
      return this.stats.applicationStatuses.reduce((sum, item) =>
          sum + parseInt(item.businessCount || 0), 0);
    },
    totalStatusValue() {
      if (!this.stats.applicationStatuses) return 0;
      return this.stats.applicationStatuses.reduce((sum, item) =>
          sum + parseFloat(item.totalValue || 0), 0);
    },
    calculateStatusPercentage() {
      return (count) => {
        if (this.totalStatusBusinesses === 0) return 0;
        return ((parseInt(count) / this.totalStatusBusinesses) * 100).toFixed(1);
      };
    },
    getStatusBadgeClass() {
      return (statusName) => {
        // Handle text-based status from transactions table
        const status = String(statusName).toLowerCase();
        if (status.includes('paid')) return 'bg-success-subtle text-success';
        if (status.includes('pending')) return 'bg-warning-subtle text-warning';
        if (status.includes('failed')) return 'bg-danger-subtle text-danger';
        if (status.includes('cancelled')) return 'bg-secondary-subtle text-secondary';
        return 'bg-info-subtle text-info';
      };
    }
  },
  mounted() {
    this.getDashboard();
    this.menuAccess();
  },
  methods: {
    menuAccess() {
      const data = new FormData();
      data.append("function", "menuAccess");
      data.append("menu", "Plans Dashboard");
      data.append("sessionNames", sessionStorage.getItem("sessionNames"));
      data.append("sessionIdNo", sessionStorage.getItem("sessionIdNo"));
      execute(data, biller).catch((e) => console.error('Menu access error:', e.message));
    },
    numberFormat(num) {
      if (!num || isNaN(num)) return '0';
      return new Intl.NumberFormat("en-GB").format(num);
    },
    growthClass(growth) {
      if (growth > 0) return 'badge-soft-success';
      if (growth < 0) return 'badge-soft-danger';
      return 'badge-soft-secondary';
    },
    getRankClass(index) {
      if (index === 0) return 'bg-warning text-white';
      if (index === 1) return 'bg-secondary text-white';
      if (index === 2) return 'bg-danger text-white';
      return 'bg-light text-dark';
    },
    getRankBadgeClass(index) {
      if (index < 3) return 'bg-warning';
      return 'bg-light text-dark';
    },
    getApprovalRateClass(rate) {
      if (rate >= 70) return 'bg-success';
      if (rate >= 50) return 'bg-warning';
      return 'bg-danger';
    },
    calculateApprovalRate(approved, total) {
      if (total === 0) return 0;
      return ((approved / total) * 100).toFixed(1);
    },

    getDashboard() {
      const data = new FormData();
      data.append("function", "getDashboardPlans");
      execute(data, econstruction)
          .then((res) => {
            if (res.data.success) {
              const responseData = res.data.data;

              // Handle both old and new structures
              if (responseData.planQuickStats) {
                this.stats = {
                  planCountSummary: responseData.planQuickStats.licenceCountSummary || responseData.planCountSummary,
                  financialSummary: responseData.planQuickStats.licenceAmountSummary || responseData.financialSummary,
                  revenuePerformance: responseData.planQuickStats.revenuePerformance || responseData.revenuePerformance || [],
                  subCountyStatistics: responseData.planQuickStats.statistics || responseData.subCountyStatistics || [],
                  topSubCounties: responseData.topSubCounties || [],
                  topOfficers: responseData.topOfficers || [],
                  paymentModes: responseData.paymentModes || [],
                  dailyTrend: responseData.dailyTrend || [],
                  topServiceTypes: responseData.topServiceTypes || [],
                  applicationStatuses: responseData.applicationStatuses || []
                };
              } else {
                this.stats = responseData;
              }

              // Render charts
              this.$nextTick(() => {
                this.renderRevenuePerformanceChart();
                this.renderPaymentModesChart();
                this.renderDailyTrendChart();
              });
            }
          })
          .catch((e) => console.error('Error:', e.message));
    },

    renderRevenuePerformanceChart() {
      Highcharts.chart('revenueChart', {
        chart: { type: 'column' },
        title: { text: null },
        xAxis: {
          categories: this.stats.revenuePerformance.map(item => item[0])
        },
        yAxis: {
          title: { text: 'Revenue (KES)' }
        },
        series: [{
          name: new Date().getFullYear(),
          data: this.stats.revenuePerformance.map(item => item[1]),
          color: '#405189'
        }, {
          name: new Date().getFullYear() - 1,
          data: this.stats.revenuePerformance.map(item => item[2]),
          color: '#f1b44c'
        }],
        credits: { enabled: false }
      });
    },

    renderPaymentModesChart() {
      const data = this.stats.paymentModes.map(mode => ({
        name: mode.payment_type,
        y: parseFloat(mode.percentage)
      }));

      Highcharts.chart('paymentModesChart', {
        chart: { type: 'pie' },
        title: { text: null },
        series: [{
          name: 'Percentage',
          data: data
        }],
        credits: { enabled: false }
      });
    },

    renderDailyTrendChart() {
      Highcharts.chart('dailyTrendChart', {
        chart: { type: 'areaspline' },
        title: { text: null },
        xAxis: {
          categories: this.stats.dailyTrend.map(item => item.collection_date)
        },
        yAxis: {
          title: { text: 'Collections (KES)' }
        },
        series: [{
          name: 'Daily Collections',
          data: this.stats.dailyTrend.map(item => parseFloat(item.daily_amount)),
          color: '#405189'
        }],
        credits: { enabled: false }
      });
    }
  }
}
</script>

<style scoped>
.card-h-100 {
  height: calc(100% - 0px);
}

.sticky-top {
  position: sticky;
  top: 0;
  z-index: 10;
  background-color: #f3f6f9 !important;
}

.table-hover tbody tr:hover {
  background-color: rgba(64, 81, 137, 0.05);
}

.progress-sm {
  height: 5px;
}
</style>